import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import {
  type ReadOneQueryArg,
  buildCountFn,
  buildCreateFn,
  buildDeleteFn,
  buildDeleteManyFn,
  buildReadManyFn,
  buildReadOneFn,
  buildUpdateFn,
  buildUpdateManyFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { Affectation } from '@/models/types';
import { aggregate, triggerFlow } from '@directus/sdk';

/** Flow Directus qui envoie à l'utilisateur un état des lieux de ses affectations. */
const AFFECTATIONS_NOTIFICATION_FLOW_ID = 'bb0d350f-c1bd-4ead-949f-217cb79e26b6';

/** Nombre d'affectations par utilisateur puis par statut. */
export type AffectationCountsByUser = Record<
  string,
  Partial<Record<Affectation['status'], number>>
>;

const affectationApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['affectation', 'affectations'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getAffectations: builder.query<Affectation[], RequestQuery<Affectation>>({
        queryFn: buildReadManyFn<Affectation>('Affectation'),
        keepUnusedDataFor: 3600, // 1 hour
        providesTags: ['affectations'],
      }),
      getAffectation: builder.query<Affectation, ReadOneQueryArg<Affectation, number>>({
        queryFn: buildReadOneFn<Affectation, number>('Affectation'),
        providesTags: ['affectation'],
      }),
      getAffectationCount: builder.query<number, RequestQuery<Affectation> | undefined>({
        queryFn: buildCountFn<Affectation>('Affectation'),
        providesTags: ['affectations'],
      }),
      getAffectationCountsByUser: builder.query<
        AffectationCountsByUser,
        RequestQuery<Affectation> | undefined
      >({
        queryFn: async query => {
          try {
            const rows = await directus.request(
              aggregate('Affectation' as never, {
                aggregate: { count: '*' },
                groupBy: ['user', 'status'] as never,
                query: query as never,
              }),
            );
            const data: AffectationCountsByUser = {};
            (
              rows as Array<{ user: string; status: Affectation['status']; count: unknown }>
            ).forEach(({ user, status, count }) => {
              data[user] = { ...data[user], [status]: Number(count) };
            });
            return { data };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
        providesTags: ['affectations'],
      }),
      createAffectation: builder.mutation<Affectation, Partial<Affectation>>({
        queryFn: buildCreateFn<Affectation>('Affectation'),
        invalidatesTags: ['affectation', 'affectations'],
      }),
      updateAffectation: builder.mutation<Affectation, { id: number; body: Partial<Affectation> }>({
        queryFn: buildUpdateFn<Affectation, number>('Affectation'),
        invalidatesTags: ['affectation', 'affectations'],
      }),
      updateAffectations: builder.mutation<
        Affectation[],
        { ids: number[]; body: Partial<Affectation> }
      >({
        queryFn: buildUpdateManyFn<Affectation, number>('Affectation'),
        invalidatesTags: ['affectation', 'affectations'],
      }),
      notifyUserAffectations: builder.mutation<void, { userId: string }>({
        queryFn: async ({ userId }) => {
          try {
            await directus.request(
              triggerFlow('POST', AFFECTATIONS_NOTIFICATION_FLOW_ID, { user_id: userId }),
            );
            return { data: undefined };
          } catch (error: unknown) {
            return { error: error as DirectusErrorType };
          }
        },
      }),
      deleteAffectation: builder.mutation<void, number>({
        queryFn: buildDeleteFn<number>('Affectation'),
        invalidatesTags: ['affectation', 'affectations'],
      }),
      deleteAffectations: builder.mutation<void, number[]>({
        queryFn: buildDeleteManyFn<number>('Affectation'),
        invalidatesTags: ['affectation', 'affectations'],
      }),
    }),
  });

export const {
  useGetAffectationsQuery,
  useGetAffectationQuery,
  useGetAffectationCountQuery,
  useGetAffectationCountsByUserQuery,
  useCreateAffectationMutation,
  useUpdateAffectationMutation,
  useUpdateAffectationsMutation,
  useNotifyUserAffectationsMutation,
  useDeleteAffectationMutation,
  useDeleteAffectationsMutation,
} = affectationApi;
